import React from 'react';
import { LEVEL_ICON_MAP } from './LevelIcons';

// LevelProgressPath
// Props:
// - levelData: optional array of { level, name } — if not provided component will render numeric levels 0..(levels-1)
// - currentLevel: integer (how many levels are active) — nodes i where i < currentLevel are active
// - spacing, radius: optional layout tweaks
const LevelProgressPath = ({ levelData = null, currentLevel = 0, spacing = 110, radius = 14 }) => {
  const levels = levelData ? levelData.length : 9; // default to 0..8 if not provided
  
  // For mobile two-row layout
  const levelsPerRow = Math.ceil(levels / 2);
  const mobileWidth = Math.max(300, (levelsPerRow - 1) * spacing + 2 * radius + 80); // Extra padding for text
  const mobileHeight = 240; // Increased height for two rows with labels
  
  // Desktop single-row layout
  const width = Math.max(300, (levels - 1) * spacing + 2 * radius + 40);
  const height = 100;

  // Helper: interpolate between two hex colors
  const hexToRgb = (hex) => {
    const cleaned = hex.replace('#', '');
    const bigint = parseInt(cleaned, 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  };

  const rgbToHex = (r, g, b) => {
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const mix = (a, b, t) => Math.round(a + (b - a) * t);

  // Preferred color source: use `levelData[i].color` when provided so colors stay consistent
  // across pages. Fallback to a shared 7-step purple palette when no color is supplied.
  const sharedPalette = [
    '#E9D5FF',
    '#D6BCFA',
    '#B794F4',
    '#9F7AEA',
    '#7C3AED',
    '#6D28D9',
    '#4C1D95'
  ];

  const colors = Array.from({ length: levels }).map((_, i) => {
    if (levelData && levelData[i] && levelData[i].color) return levelData[i].color;
    return sharedPalette[i] || sharedPalette[sharedPalette.length - 1];
  });

  // Helper to render a node
  const renderNode = (i, cx, cy, isAbove) => {
    const outerR = radius + 6;
    const labelY = isAbove ? cy - outerR - 14 : cy + outerR + 18;
    const name = levelData ? String(levelData[i]?.name ?? `Level ${i}`) : String(i);
    const isCurrent = i === currentLevel;

    // helper to darken a hex color by a percentage (0..1)
    const darkenHex = (hex, pct) => {
      if (!hex) return hex;
      const cleaned = hex.replace('#', '');
      const bigint = parseInt(cleaned, 16);
      let r = (bigint >> 16) & 255;
      let g = (bigint >> 8) & 255;
      let b = bigint & 255;
      r = Math.max(0, Math.min(255, Math.round(r * (1 - pct))));
      g = Math.max(0, Math.min(255, Math.round(g * (1 - pct))));
      b = Math.max(0, Math.min(255, Math.round(b * (1 - pct))));
      const toHex = (v) => v.toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const baseColor = colors[i] || sharedPalette[sharedPalette.length - 1];
    const outerFill = isCurrent ? darkenHex(baseColor, 0.18) : baseColor;
    const outerOpacity = isCurrent ? 1 : 0.35;
    const innerFill = isCurrent ? '#ffffff' : '#F3F4F6';
    const iconColor = isCurrent ? darkenHex(baseColor, 0.35) : baseColor;
    const labelColor = isCurrent ? darkenHex(baseColor, 0.45) : baseColor;

    return (
      <g key={`node-${i}`} transform={`translate(${cx}, ${cy})`}>
        <circle
          r={outerR}
          fill={outerFill}
          style={{ transition: 'fill 300ms ease, opacity 300ms ease', opacity: outerOpacity }}
        />

        <circle
          r={radius}
          fill={innerFill}
          style={{ transition: 'fill 300ms ease, r 300ms ease' }}
        />

        {/* icon placed centered inside the inner circle */}
        {LEVEL_ICON_MAP[i] && (
          (() => {
            const IconComp = LEVEL_ICON_MAP[i];
            const iconSize = Math.round(radius * 1.6);
            return (
              <g transform={`translate(${-iconSize / 2}, ${-iconSize / 2})`}>
                <IconComp size={iconSize} color={iconColor} />
              </g>
            );
          })()
        )}

        {/* show level name as primary label (alternating up/down) */}
        <text x={0} y={isAbove ? labelY - cy : labelY - cy} textAnchor="middle" fontSize={13} fill={labelColor} fontWeight={600}>
          {name}
        </text>
      </g>
    );
  };

  return (
    <div className="w-full flex justify-center my-6">
      {/* Desktop view - single row */}
      <svg viewBox={`0 0 ${width} ${height}`} className="hidden md:block max-w-full h-28" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="soft-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.08" />
          </filter>
        </defs>

        {/* connecting segments */}
        {Array.from({ length: levels - 1 }).map((_, i) => {
          const x1 = radius + 20 + i * spacing;
          const x2 = radius + 20 + (i + 1) * spacing;
          const filled = i < currentLevel - 1;
          return (
            <line
              key={`seg-${i}`}
              x1={x1}
              y1={height / 2}
              x2={x2}
              y2={height / 2}
              stroke={filled ? colors[i] : '#E6E6E9'}
              strokeWidth={6}
              strokeLinecap="round"
              style={{ transition: 'stroke 400ms ease' }}
            />
          );
        })}

        {/* nodes + labels (alternating up/down) */}
        {Array.from({ length: levels }).map((_, i) => {
          const cx = radius + 20 + i * spacing;
          const cy = height / 2;
          const isAbove = i % 2 === 0;
          return renderNode(i, cx, cy, isAbove);
        })}
      </svg>

      {/* Mobile view - two rows */}
      <svg viewBox={`0 0 ${mobileWidth} ${mobileHeight}`} className="md:hidden max-w-full h-auto" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="soft-shadow-mobile" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.08" />
          </filter>
        </defs>

        {/* First row - levels 0 to levelsPerRow-1 (left to right) */}
        {Array.from({ length: levelsPerRow - 1 }).map((_, i) => {
          if (i >= levels) return null;
          const x1 = radius + 40 + i * spacing;
          const x2 = radius + 40 + (i + 1) * spacing;
          const filled = i < currentLevel - 1;
          return (
            <line
              key={`seg-row1-${i}`}
              x1={x1}
              y1={60}
              x2={x2}
              y2={60}
              stroke={filled ? colors[i] : '#E6E6E9'}
              strokeWidth={6}
              strokeLinecap="round"
              style={{ transition: 'stroke 400ms ease' }}
            />
          );
        })}

        {Array.from({ length: levelsPerRow }).map((_, i) => {
          if (i >= levels) return null;
          const cx = radius + 40 + i * spacing;
          const cy = 60;
          const isAbove = true;
          return renderNode(i, cx, cy, isAbove);
        })}

        {/* Vertical connector from last node of first row to first node of second row */}
        {levels > levelsPerRow && (
          <line
            key="vertical-connector"
            x1={radius + 40 + (levelsPerRow - 1) * spacing}
            y1={60 + radius + 6}
            x2={radius + 40 + (levelsPerRow - 1) * spacing}
            y2={180 - radius - 6}
            stroke={currentLevel > levelsPerRow ? colors[levelsPerRow - 1] : '#E6E6E9'}
            strokeWidth={6}
            strokeLinecap="round"
            style={{ transition: 'stroke 400ms ease' }}
          />
        )}

        {/* Second row - remaining levels (LEFT TO RIGHT - normal order) */}
        {Array.from({ length: levels - levelsPerRow - 1 }).map((_, i) => {
          const actualIndex = levelsPerRow + i;
          const nextIndex = actualIndex + 1;
          const x1 = radius + 40 + (levelsPerRow - 1) * spacing - i * spacing;
          const x2 = radius + 40 + (levelsPerRow - 1) * spacing - (i + 1) * spacing;
          const filled = actualIndex < currentLevel - 1;
          return (
            <line
              key={`seg-row2-${i}`}
              x1={x1}
              y1={180}
              x2={x2}
              y2={180}
              stroke={filled ? colors[actualIndex] : '#E6E6E9'}
              strokeWidth={6}
              strokeLinecap="round"
              style={{ transition: 'stroke 400ms ease' }}
            />
          );
        })}

        {Array.from({ length: levels - levelsPerRow }).map((_, i) => {
          const actualIndex = levelsPerRow + i;
          const cx = radius + 40 + (levelsPerRow - 1 - i) * spacing;
          const cy = 180;
          const isAbove = false;
          return renderNode(actualIndex, cx, cy, isAbove);
        })}
      </svg>
    </div>
  );
};

export default LevelProgressPath;
